import React, { useEffect, useState } from 'react';
import { Table, Button, message, Card, Space, Typography, Select, Modal, Form, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import { listUsers, toggleTranscribe, fetchStats, updatePlan, updateUsage, resetUsage } from '../api/admin';

const { Title } = Typography;

function AdminPage({ user }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      if (data.success) setUsers(data.users);
    } catch (err) {
      message.error(t('admin.fetchUserListFail'));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await fetchStats();
      if (data.success) setStats(data.stats);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadUsers();
    loadStats();
  }, []);

  const handleToggle = async (record) => {
    const enable = !record.canTranscribe;
    try {
      const data = await toggleTranscribe(record._id, enable);
      if (data.success) {
        message.success(t('common.updateSuccess'));
        loadUsers();
      } else {
        message.error(data.message || '更新失败');
      }
    } catch (err) {
      message.error(t('common.requestFail'));
    }
  };

  const PLAN_LIMIT = {
    free: { minutes: 0, subtitleCreates: 30, subtitleEdits: 100, ruleCreates: 10 },
    pro:  { minutes: 400, subtitleCreates: 300, subtitleEdits: 1000, ruleCreates: 100 },
  };

  const handlePlanChange = async (userId, plan) => {
    const hide = message.loading(t('common.updating'));
    try {
      const data = await updatePlan(userId, plan);
      if (data.success) {
        message.success(t('admin.planUpdated'));
        loadUsers();
      } else {
        message.error(data.message || '更新失败');
      }
    } catch (err) {
      message.error(t('common.requestFail'));
    } finally {
      hide();
    }
  };

  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const openQuotaModal = (user)=>{
    setEditingUser(user);
    form.resetFields();
  };
  const resetQuota = (user)=>{
    Modal.confirm({
      title:`重置 ${user.displayName} 的用量？`,
      onOk: async ()=>{
        const hide=message.loading(t('common.updating'));
        try{
          await resetUsage(user._id);
          message.success(t('admin.resetSuccess'));
          loadUsers();
        }finally{hide();}
      }
    });
  };

  const handleSaveQuota = async ()=>{
    await form.validateFields();
    let payload = form.getFieldsValue();
    payload = Object.fromEntries(Object.entries(payload).filter(([_,v])=>v!==undefined && v!==null && v!==''));
    if(Object.keys(payload).length===0){message.info(t('admin.noValues'));return;}
    const hide= message.loading(t('common.saving'));
    try{
      const res = await updateUsage(editingUser._id, payload);
      if(res.success){
        message.success(t('admin.quotaUpdated'));
        loadUsers();
        setEditingUser(null);
      }else{
        message.error(res.message||'更新失败');
      }
    }catch(e){message.error(t('common.requestFail'));}
    hide();
  };

  const columns = [
    { title: t('admin.nickname'), dataIndex: 'displayName' },
    { title: t('admin.email'), dataIndex: 'email' },
    { title: t('admin.role'), dataIndex: 'role' },
    { title: t('admin.plan'), dataIndex: 'plan', render: (plan, record) => (
        <Select size="small" value={plan} onChange={(val)=>handlePlanChange(record._id, val)}>
          <Select.Option value="free">Free</Select.Option>
          <Select.Option value="pro">Pro</Select.Option>
        </Select>
      ) },
      { title: t('admin.remainingMinutes'), render: (_, record)=>{
          const total = (PLAN_LIMIT[record.plan]?.minutes ?? 0) + (record.extra?.minutes||0);
          return total===Infinity? '∞' : total - (record.usage.minutes||0);
      } },
      { title: t('admin.remainingSubtitles'), render: (_, record)=>{
          const total=(PLAN_LIMIT[record.plan].subtitleCreates||0)+(record.extra?.subtitleCreates||0);
          return <span>{total - (record.usage.subtitleCreates||0)}</span>;
      } },
      { title: t('admin.remainingEdits'), render: (_, record)=>{
          const total = PLAN_LIMIT[record.plan].subtitleEdits + (record.extra?.subtitleEdits||0);
          return <span>{total - (record.usage.subtitleEdits||0)}</span>;
      } },
      { title: t('admin.remainingRules'), render: (_, record)=>{
          const total = PLAN_LIMIT[record.plan].ruleCreates + (record.extra?.ruleCreates||0);
          return <span>{total - (record.usage.ruleCreates||0)}</span>;
      } },
      { title: t('admin.canTranscribe'), dataIndex: 'canTranscribe', render: (v) => (v ? t('common.yes') : t('common.no')) },
    {
      title: t('common.actions'),
      render: (_, record) => (
        <Space>
            <Button size="small" onClick={()=>openQuotaModal(record)}>{t('admin.giveQuota')}</Button>
          <Button size="small" danger style={{marginLeft:4}} onClick={()=>resetQuota(record)}>{t('admin.resetQuota')}</Button>
            <Button size="small" onClick={() => handleToggle(record)}>
          {record.canTranscribe ? t('admin.disableTranscribe') : t('admin.enableTranscribe')}
        </Button>
            </Space>
      ),
    },
  ];

  return (
    <div style={{ margin: 24 }}>
      <Title level={3}>{t('admin.title')}</Title>
      {stats && (
        <Space style={{ marginBottom: 16 }}>
          <Card title={t('admin.totalUsers')} bordered={false}>{stats.userCount}</Card>
          <Card title={t('admin.subtitleCount')} bordered={false}>{stats.subtitleCount}</Card>
        </Space>
      )}
      <Table rowKey="_id" dataSource={users} columns={columns} loading={loading} />
          <Modal
        title={`${t('admin.adjustQuota')} - ${editingUser?.displayName}`}
        open={!!editingUser}
        onOk={handleSaveQuota}
        onCancel={()=>setEditingUser(null)}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('admin.giveMinutes')} name="minutes" rules={[{type:'number',min:0}]}> <InputNumber min={0} onChange={(v)=>form.setFieldValue('minutes',v)}/> </Form.Item>
          <Form.Item label={t('admin.giveSubtitleCreates')} name="subtitleCreates" rules={[{type:'number',min:0}]}> <InputNumber min={0} onChange={(v)=>form.setFieldValue('subtitleCreates',v)}/> </Form.Item>
          <Form.Item label={t('admin.giveSubtitleEdits')} name="subtitleEdits" rules={[{type:'number',min:0}]}> <InputNumber min={0} onChange={(v)=>form.setFieldValue('subtitleEdits',v)}/> </Form.Item>
          <Form.Item label={t('admin.giveRuleCreates')} name="ruleCreates" rules={[{type:'number',min:0}]}> <InputNumber min={0} onChange={(v)=>form.setFieldValue('ruleCreates',v)}/> </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminPage;
